import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export type CardRadius = 'card' | 'panel' | 'hero';
export type CardLift = 'none' | 'ride' | 'route';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** 14px list cards, 16px detail panels, 18px hero surfaces. */
  radius?: CardRadius;
  /**
   * Hover treatment for clickable cards. `ride` is the 1px lift used in search
   * results and trips; `route` is the slightly stronger 2px lift on the home
   * page's popular-route tiles.
   */
  lift?: CardLift;
  /** Removes the default padding so the card can host a full-bleed map. */
  flush?: boolean;
}

const RADIUS: Record<CardRadius, string> = {
  card: 'rounded-card',
  panel: 'rounded-panel',
  hero: 'rounded-hero',
};

const LIFT: Record<CardLift, string> = {
  none: '',
  ride: 'cursor-pointer transition-[transform,box-shadow] duration-fast ease-standard hover:-translate-y-px hover:shadow-ride',
  route:
    'cursor-pointer transition-[transform,box-shadow] duration-base ease-standard hover:-translate-y-0.5 hover:shadow-route',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    radius = 'card',
    lift = 'none',
    flush = false,
    className,
    children,
    onClick,
    onKeyDown,
    ...rest
  },
  ref,
) {
  /**
   * A card that does something is a control, and has to behave like one.
   *
   * The export draws these as `<div onClick>` — a search result, a popular
   * route, a row in the trips dashboard. With a pointer that works; from a
   * keyboard the card cannot be reached at all, which leaves every ride on the
   * search screen unopenable without a mouse (WCAG 2.1.1). Giving it button
   * semantics and Enter/Space costs nothing visually.
   *
   * A card that is only a container stays a plain `<div>`, so nothing that is
   * not interactive gains a tab stop.
   */
  const interactive = onClick !== undefined;

  return (
    <div
      ref={ref}
      className={cn(
        'border border-line bg-surface',
        RADIUS[radius],
        LIFT[lift],
        flush ? 'overflow-hidden' : 'p-[18px]',
        className,
      )}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (!interactive || event.defaultPrevented) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space scrolls the page on anything that is not a real button, so the
        // default has to go before the click is raised.
        event.preventDefault();
        // Dispatching a real click rather than calling the handler with the
        // keyboard event: `onClick` is typed for a MouseEvent and every caller
        // is entitled to read one.
        event.currentTarget.click();
      }}
      {...rest}
    >
      {children}
    </div>
  );
});

/**
 * The dark ngahere panel used for impact figures — community CO₂ on the home
 * page, lifetime CO₂ on the profile, the saved figure on the confirmation.
 */
export const EcoPanel = forwardRef<HTMLDivElement, Omit<CardProps, 'lift'>>(function EcoPanel(
  { radius = 'panel', flush = false, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'relative overflow-hidden bg-ngahere text-white',
        RADIUS[radius],
        flush ? '' : 'p-[22px]',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});
